import { useState } from "react";
import { ArrowUpRight, Check, Gift } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NewsletterSignup = ({
  source = "site",
  guide = "reflexologie",
  title = "Recevez « 3 gestes de réflexologie à faire chez soi »",
  description = "Un petit guide gratuit, et les prochaines dates de mes formations, ateliers et séjours — directement dans votre boîte mail. Sans spam, promis.",
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      const res = await fetch(`${API}/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name || null, email, source, guide }),
      });
      if (!res.ok) throw new Error();
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="px-6 lg:px-12 py-20" data-testid="newsletter-signup">
      <div className="max-w-5xl mx-auto overflow-hidden rounded-3xl border border-olive-border bg-olive-dark text-beige-100">
        <div className="grid lg:grid-cols-2">
          <div className="p-10 lg:p-12">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full bg-beige-100/10 px-4 py-2 text-xs uppercase tracking-[0.2em] text-gold">
                <Gift className="w-4 h-4" strokeWidth={1.5} /> Guide offert
              </span>
              <h2 className="mt-5 font-serif text-3xl sm:text-4xl font-light leading-tight">
                {title}
              </h2>
              <p className="mt-4 text-beige-300/90 leading-relaxed">
                {description}
              </p>
            </Reveal>
          </div>

          <div className="bg-beige-100/5 p-10 lg:p-12 flex items-center">
            {status === "done" ? (
              <div className="w-full text-center" data-testid="newsletter-success">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gold text-olive-dark">
                  <Check className="w-7 h-7" strokeWidth={2} />
                </span>
                <p className="mt-5 font-serif text-2xl">Merci, c'est fait !</p>
                <p className="mt-2 text-beige-300/90 text-sm leading-relaxed">
                  Votre guide vient de partir par email. Pensez à vérifier vos spams s'il n'arrive pas tout de suite 🌿
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="w-full space-y-4" data-testid="newsletter-form">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Votre prénom (facultatif)"
                  data-testid="newsletter-name"
                  className="w-full rounded-xl border border-beige-100/20 bg-beige-100/5 px-5 py-3.5 text-beige-100 placeholder-beige-300/50 outline-none transition-colors focus:border-gold"
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Votre e-mail"
                  data-testid="newsletter-email"
                  className="w-full rounded-xl border border-beige-100/20 bg-beige-100/5 px-5 py-3.5 text-beige-100 placeholder-beige-300/50 outline-none transition-colors focus:border-gold"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="newsletter-submit"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-beige-100 px-8 py-4 text-sm font-medium text-olive-dark transition-all duration-300 hover:-translate-y-0.5 hover:bg-beige-50 disabled:opacity-60"
                >
                  {status === "loading" ? "Envoi…" : "Recevoir mon guide"}
                  {status !== "loading" && <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />}
                </button>
                {status === "error" && (
                  <p className="text-sm text-gold" data-testid="newsletter-error">
                    Une erreur est survenue. Réessayez dans un instant.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
